// src/pages/Analytics.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Container,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Link as LinkIcon,
  Settings as SettingsIcon,
  CalendarToday as CalendarIcon,
  ArrowUpward as ArrowUpwardIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { useGoogleLogin } from '@react-oauth/google';
import { DataGrid } from '@mui/x-data-grid';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Tab } from '@mui/material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { motion } from 'framer-motion';
import { getApiUrl } from '../config';

function Analytics() {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [openSetupDialog, setOpenSetupDialog] = useState(false);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [topPosts, setTopPosts] = useState([]);
  const [dateRange, setDateRange] = useState('last30days');
  const [activeTab, setActiveTab] = useState('1');
  const [trafficSources, setTrafficSources] = useState([]);

  useEffect(() => {
    checkGAConnection();
  }, []);

  useEffect(() => {
    if (isConnected) {
      checkGAConnection();
      fetchTopPosts();
      fetchTrafficSources();
    }
  }, [isConnected, dateRange]);

  const checkGAConnection = async () => {
    try {
      console.log('Checking GA4 Connection...');
      const response = await fetch(`${getApiUrl()}/settings/ga4-status?range=${dateRange}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      const data = await response.json();
      console.log('GA4 Status Response:', data);

      if (data.error === 'No refresh token available' || data.error === 'Failed to refresh token') {
        setIsConnected(false);
        setError('Google Analytics connection expired. Please reconnect.');
        return;
      }

      setIsConnected(data.connected);
      if (data.connected && data.analytics) {
        setAnalyticsData(data.analytics);
      }
    } catch (err) {
      console.error('GA4 Status Check Error:', err);
      setError('Failed to fetch analytics data');
      setIsConnected(false);
    }
  };

  const fetchTopPosts = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/analytics/top-posts?range=${dateRange}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      const data = await response.json();
      setTopPosts(data.posts);
    } catch (err) {
      console.error('Error fetching top posts:', err);
      setError('Failed to fetch top posts data');
    }
  };

  const fetchTrafficSources = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/analytics/traffic-sources?range=${dateRange}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      const data = await response.json();
      if (data.sources) {
        setTrafficSources(data.sources);
      }
    } catch (err) {
      console.error('Error fetching traffic sources:', err);
      setError('Failed to fetch traffic sources data');
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    scope: [
      'https://www.googleapis.com/auth/analytics.readonly',
      'https://www.googleapis.com/auth/analytics.edit',
      'openid',
      'https://www.googleapis.com/auth/userinfo.profile',
      'https://www.googleapis.com/auth/userinfo.email',
      'https://www.googleapis.com/auth/analytics'
    ].join(' '),
    flow: 'auth-code',
    redirect_uri: 'https://autoblogpilot.com',
    onSuccess: async (codeResponse) => {
      try {
        console.log('Google OAuth Success:', codeResponse);

        // Exchange the code for tokens
        const tokensResponse = await fetch(`${getApiUrl()}/settings/exchange-google-code`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            code: codeResponse.code
          })
        });

        const tokens = await tokensResponse.json();
        if (!tokensResponse.ok) {
          throw new Error(tokens.error || 'Failed to exchange code for tokens');
        }

        // Store the tokens
        const response = await fetch(`${getApiUrl()}/settings/store-ga4`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(tokens)
        });

        const data = await response.json();
        console.log('Store GA4 Response:', data);

        if (!response.ok) {
          throw new Error(data.error || 'Failed to store GA4 settings');
        }
        
        setIsConnected(true);
        checkGAConnection();
      } catch (err) {
        console.error('GA4 Storage Error:', err);
        setError(err.message);
      }
    },
    onError: (errorResponse) => {
      console.error('Google OAuth Error:', errorResponse);
      setError('Google login failed');
    }
  });

  const handleStartSetup = () => {
    handleGoogleLogin();
  };

  const renderSetupInstructions = () => {
    return (
      <>
        <Typography variant="body1" gutterBottom>
          Your GA4 tracking code will be automatically added to your blog posts.
        </Typography>
        <Typography variant="body1" gutterBottom>
          Click "Finish" to complete the setup.
        </Typography>
      </>
    );
  };

  const handleReconnectGA4 = async () => {
    try {
      setError(null);
      handleGoogleLogin();
    } catch (err) {
      console.error('Error reconnecting GA4:', err);
      setError('Failed to reconnect to Google Analytics');
    }
  };

  if (!analyticsData) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            background: 'linear-gradient(145deg, #f6f8ff 0%, #f0f4ff 100%)',
            borderRadius: 4,
            p: 4,
            minHeight: '90vh',
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box mb={4}>
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Analytics
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Track and analyze your blog's performance
              </Typography>
            </Box>

            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
                action={
                  error.includes('expired') && (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleReconnectGA4}
                    >
                      Reconnect
                    </Button>
                  )
                }
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            <Paper
              elevation={2}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                background: 'white',
                transition: 'all 0.3s ease',
              }}
            >
              <Box p={3}>
                <Box display="flex" alignItems="center" mb={3}>
                  <Box
                    sx={{
                      background: isConnected ? 'rgba(76, 175, 80, 0.1)' : 'rgba(33, 150, 243, 0.1)',
                      borderRadius: '50%',
                      p: 1,
                      mr: 2,
                      color: isConnected ? 'success.main' : 'primary.main',
                    }}
                  >
                    <AnalyticsIcon />
                  </Box>
                  <Box flex={1}>
                    <Typography variant="h5" component="h2" fontWeight="bold" gutterBottom>
                      Google Analytics 4
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {isConnected ? (
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinkIcon fontSize="small" sx={{ color: 'success.main' }} />
                          Connected and waiting for data
                        </Box>
                      ) : (
                        'Connect Google Analytics 4 to view your blog\'s performance metrics'
                      )}
                    </Typography>
                  </Box>
                  <Box>
                    {isConnected ? (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Update Settings">
                          <IconButton
                            onClick={handleStartSetup}
                            sx={{ 
                              color: 'primary.main',
                              '&:hover': { background: 'rgba(33, 150, 243, 0.1)' }
                            }}
                          >
                            <SettingsIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Refresh Data">
                          <IconButton
                            onClick={checkGAConnection}
                            sx={{ 
                              color: 'primary.main',
                              '&:hover': { background: 'rgba(33, 150, 243, 0.1)' }
                            }}
                          >
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={handleStartSetup}
                        startIcon={<LinkIcon />}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                          boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                        }}
                      >
                        Connect GA4
                      </Button>
                    )}
                  </Box>
                </Box>

                {isConnected && (
                  <Box mt={3}>
                    <Divider sx={{ mb: 3 }} />
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      Initial Setup Status
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gap: 2,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(33, 150, 243, 0.05)',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                          }}
                        />
                        <Typography>Waiting for data to populate</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        It may take up to 24 hours for data to appear after the initial setup. Your GA4 tracking code will be automatically added to your blog posts.
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Paper>
          </motion.div>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          background: 'linear-gradient(145deg, #f6f8ff 0%, #f0f4ff 100%)',
          borderRadius: 4,
          p: 4,
          minHeight: '90vh',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Analytics Dashboard
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Track your blog's performance metrics
              </Typography>
            </Box>
            <Box>
              <ToggleButtonGroup
                value={dateRange}
                exclusive
                onChange={(e, newValue) => newValue && setDateRange(newValue)}
                size="small"
                sx={{
                  bgcolor: 'white',
                  '& .MuiToggleButton-root.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  },
                }}
              >
                <ToggleButton value="last7days">7 Days</ToggleButton>
                <ToggleButton value="last30days">30 Days</ToggleButton>
                <ToggleButton value="last90days">90 Days</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          <TabContext value={activeTab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <TabList 
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    minWidth: 120,
                  },
                  '& .Mui-selected': {
                    fontWeight: 'bold',
                  },
                }}
              >
                <Tab label="Overview" value="1" />
                <Tab label="Top Posts" value="2" />
                <Tab label="Traffic Sources" value="3" />
              </TabList>
            </Box>

            <TabPanel value="1" sx={{ p: 0 }}>
              <Grid container spacing={3}>
                {analyticsData.map((metric) => (
                  <Grid item xs={12} md={6} lg={4} key={metric.id}>
                    <Paper
                      elevation={2}
                      sx={{
                        borderRadius: 3,
                        p: 3,
                        height: '100%',
                        background: 'white',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" mb={2}>
                        <Box
                          sx={{
                            background: 'rgba(33, 150, 243, 0.1)',
                            borderRadius: '50%',
                            p: 1,
                            mr: 2,
                            color: 'primary.main',
                          }}
                        >
                          <TrendingUpIcon />
                        </Box>
                        <Typography variant="h6">{metric.name}</Typography>
                      </Box>
                      <Typography variant="h3" color="primary.main" fontWeight="bold">
                        {metric.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {metric.change > 0 ? '+' : ''}{metric.change}% vs last period
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </TabPanel>

            <TabPanel value="2" sx={{ p: 0 }}>
              <Paper
                elevation={2}
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                }}
              >
                <Box p={3}>
                  <Typography variant="h6" gutterBottom>Top Performing Posts</Typography>
                  <DataGrid
                    rows={topPosts.map((post, index) => ({
                      ...post,
                      id: post.id || index, // Ensure each row has an id
                    }))}
                    columns={[
                      { 
                        field: 'title', 
                        headerName: 'Post Title', 
                        flex: 2,
                        renderCell: (params) => (
                          <Typography noWrap>{params.value || 'N/A'}</Typography>
                        ),
                      },
                      { 
                        field: 'pageViews', 
                        headerName: 'Page Views',
                        flex: 1,
                        renderCell: (params) => (
                          <Box display="flex" alignItems="center" gap={1}>
                            <ArrowUpwardIcon color="success" fontSize="small" />
                            <Typography>
                              {(params.value || 0).toLocaleString()}
                            </Typography>
                          </Box>
                        ),
                      },
                      { 
                        field: 'uniqueViews', 
                        headerName: 'Unique Views',
                        flex: 1,
                        renderCell: (params) => (
                          <Typography>
                            {(params.value || 0).toLocaleString()}
                          </Typography>
                        ),
                      },
                      { 
                        field: 'avgTimeOnPage', 
                        headerName: 'Avg. Time',
                        flex: 1,
                        renderCell: (params) => (
                          <Typography>
                            {params.value ? `${params.value}s` : 'N/A'}
                          </Typography>
                        ),
                      },
                    ]}
                    autoHeight
                    pageSize={10}
                    disableSelectionOnClick
                    sx={{
                      border: 'none',
                      '& .MuiDataGrid-cell': {
                        borderBottom: '1px solid rgba(224, 224, 224, 0.4)',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        bgcolor: 'rgba(0, 0, 0, 0.02)',
                        borderBottom: 'none',
                      },
                    }}
                  />
                </Box>
              </Paper>
            </TabPanel>

            <TabPanel value="3" sx={{ p: 0 }}>
              <Paper
                elevation={2}
                sx={{
                  borderRadius: 3,
                  p: 3,
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6">Traffic Sources</Typography>
                  <PublicIcon color="primary" />
                </Box>
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={trafficSources.map(source => ({
                        ...source,
                        sessions: source.sessions || 0,
                        users: source.users || 0,
                      }))}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 60
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="source" 
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="sessions" name="Sessions" fill="#2196F3" />
                      <Bar dataKey="users" name="Users" fill="#4CAF50" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" gutterBottom>Detailed Breakdown</Typography>
                  <DataGrid
                    rows={trafficSources.map((source, index) => ({
                      ...source,
                      id: source.id || index, // Ensure each row has an id
                    }))}
                    columns={[
                      { 
                        field: 'source', 
                        headerName: 'Source', 
                        flex: 2,
                        renderCell: (params) => (
                          <Typography>{params.value || 'N/A'}</Typography>
                        ),
                      },
                      { 
                        field: 'sessions', 
                        headerName: 'Sessions', 
                        flex: 1,
                        renderCell: (params) => (
                          <Typography>
                            {(params.value || 0).toLocaleString()}
                          </Typography>
                        ),
                      },
                      { 
                        field: 'users', 
                        headerName: 'Users', 
                        flex: 1,
                        renderCell: (params) => (
                          <Typography>
                            {(params.value || 0).toLocaleString()}
                          </Typography>
                        ),
                      },
                    ]}
                    autoHeight
                    pageSize={5}
                    disableSelectionOnClick
                    sx={{
                      border: 'none',
                      '& .MuiDataGrid-cell': {
                        borderBottom: '1px solid rgba(224, 224, 224, 0.4)',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        bgcolor: 'rgba(0, 0, 0, 0.02)',
                        borderBottom: 'none',
                      },
                    }}
                  />
                </Box>
              </Paper>
            </TabPanel>
          </TabContext>
        </motion.div>
      </Box>
    </Container>
  );
}

export default Analytics;
